import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { amazonService } from '../utils/amazonService';
import { AMAZON_CONNECTION_QUERY_KEY } from '../hooks/useAmazonConnection';

export const AmazonCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code') || searchParams.get('spapi_oauth_code');
    const state = searchParams.get('state');
    const amazonError = searchParams.get('error');

    if (amazonError) {
      setError(searchParams.get('error_description') || 'Amazon authorization was cancelled.');
      return;
    }

    if (!code || !state) {
      setError('Missing authorization details. Start again from Settings.');
      return;
    }

    let cancelled = false;
    amazonService
      .completeAuthorization(code, state)
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: AMAZON_CONNECTION_QUERY_KEY });
        if (!cancelled) {
          navigate('/settings?tab=amazon', { replace: true });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to connect Amazon account.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams, queryClient]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={40} />
          <h1 className="text-xl font-semibold text-white mb-2">Amazon connection failed</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/settings?tab=amazon', { replace: true })}
            className="bg-cashy-purple-500 hover:bg-cashy-purple-600 text-white px-6 py-3 rounded-lg"
          >
            Back to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cashy-purple-500 mx-auto mb-4" />
        <p className="text-gray-400">Connecting Amazon account…</p>
      </div>
    </div>
  );
};
